import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, CheckCircle } from "lucide-react";
import { manufacturingImages } from "../data/mockData";

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 35 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const JewelryManufacturingPage = () => {
  const capabilities = [
    "Eternity Bands & Wedding Rings",
    "Engagement Rings (Solitaire, Halo, Three-Stone)",
    "Fashion Rings & Statement Pieces",
    "Tennis Bracelets & Bangles",
    "Pendants & Necklaces",
    "Diamond Stud & Drop Earrings",
    "Custom & Bespoke Designs",
    "White-Label Manufacturing"
  ];

  const process = [
    {
      step: "01",
      title: "Concept & CAD Modeling",
      desc: "Your idea becomes a digital blueprint. Our designers use advanced 3D CAD software to create hyper-detailed models—exploring symmetry, stone placement, and structural integrity before any metal is touched."
    },
    {
      step: "02",
      title: "Prototyping & Casting",
      desc: "A high-fidelity wax prototype is hand-inspected, then investment-cast in your chosen precious metal. We use vacuum-assisted casting to eliminate porosity, ensuring a flawless foundation."
    },
    {
      step: "03",
      title: "Stone Setting & Assembly",
      desc: "Our master setters work under 10x magnification, securing each stone with proprietary low-profile prongs engineered for maximum light return and all-day comfort."
    },
    {
      step: "04",
      title: "Polish, QC & Certification",
      desc: "Every finished piece passes through an eight-checkpoint quality assurance protocol—covering dimensional accuracy, stone security, surface finish, and hallmark compliance—before earning our seal."
    }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-[#1a1a1a]">
        <div className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${manufacturingImages.jewelry[1]})` }} />
        <div className="absolute inset-0 bg-black/50" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }} className="relative z-10 text-center px-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Jewelry Manufacturing
          </h1>
          <p className="mt-4 text-white/60 text-sm tracking-[0.1em]"
            style={{ fontFamily: "'Inter', sans-serif" }}>
            Where Precision Meets Artistry
          </p>
          <div className="h-[1px] w-16 bg-[#c8a96e] mx-auto mt-6" />
        </motion.div>
      </section>

      {/* Intro */}
      <section className="py-24 bg-[#faf9f7]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h3 className="text-[12px] tracking-[0.3em] uppercase text-[#c8a96e] mb-4"
                  style={{ fontFamily: "'Inter', sans-serif" }}>Our New York Atelier</h3>
                <h2 className="text-3xl md:text-4xl text-[#1a1a1a] font-light"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Where Heritage Shapes the Future
                </h2>
                <p className="mt-6 text-[#555] text-[15px] leading-[1.8]"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  Nestled in Midtown Manhattan, our atelier blends old-world goldsmithing traditions
                  with cutting-edge technology. A team of over 40 artisans—each with a minimum of
                  a decade of bench experience—transforms raw metal and stone into wearable works of art.
                </p>
                <p className="mt-4 text-[#555] text-[15px] leading-[1.8]"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  Our signature comfort-fit architecture maximizes diamond visibility through
                  lower-profile settings while ensuring secure stone retention and effortless wearability.
                  The result: pieces that feel as exceptional as they look.
                </p>
              </div>
              <div className="relative">
                <img src={manufacturingImages.jewelry[0]} alt="Jewelry Workshop"
                  className="w-full h-[400px] object-cover" loading="lazy" />
                <div className="absolute -bottom-4 -right-4 w-20 h-20 border-2 border-[#c8a96e]" />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-[42px] text-[#1a1a1a] font-light"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Our Capabilities
              </h2>
              <p className="mt-4 text-[#888] text-[15px]" style={{ fontFamily: "'Inter', sans-serif" }}>
                Everything under one roof—from concept to completion
              </p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {capabilities.map((item, i) => (
              <FadeIn key={item} delay={i * 0.05}>
                <div className="flex items-center gap-4 p-6 bg-[#faf9f7] hover:bg-[#1a1a1a] group transition-all duration-400">
                  <CheckCircle className="w-5 h-5 text-[#c8a96e] flex-shrink-0" />
                  <span className="text-[#555] group-hover:text-white text-[15px] transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}>
                    {item}
                  </span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-[#1a1a1a]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <div className="text-center mb-20">
              <h2 className="text-3xl md:text-4xl lg:text-[42px] text-white font-light"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Our Process
              </h2>
              <p className="mt-4 text-white/50 text-[15px]" style={{ fontFamily: "'Inter', sans-serif" }}>
                Four stages. Zero compromises.
              </p>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {process.map((item, i) => (
              <FadeIn key={item.step} delay={i * 0.1}>
                <div className="text-center">
                  <span className="text-6xl font-light text-[#c8a96e]/20 block mb-2"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {item.step}
                  </span>
                  <h3 className="text-xl text-white mb-4"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {item.title}
                  </h3>
                  <p className="text-white/55 text-[14px] leading-[1.7]"
                    style={{ fontFamily: "'Inter', sans-serif" }}>
                    {item.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 bg-[#faf9f7]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl text-[#1a1a1a] font-light"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Inside the Workshop
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {manufacturingImages.jewelry.map((img, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="overflow-hidden group">
                  <img src={img} alt={`Workshop ${i + 1}`}
                    className="w-full h-[300px] object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy" />
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white text-center">
        <FadeIn>
          <h2 className="text-3xl md:text-4xl text-[#1a1a1a] font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Ready to Bring Your Vision to Life?
          </h2>
          <p className="mt-4 text-[#888] text-[15px]" style={{ fontFamily: "'Inter', sans-serif" }}>
            Fast turnaround · 10-14 business days · Made in USA
          </p>
          <Link to="/contact-us"
            className="inline-flex items-center gap-2 mt-8 px-8 py-3 border border-[#c8a96e] text-[#c8a96e] text-[12px] tracking-[0.15em] uppercase hover:bg-[#c8a96e] hover:text-[#1a1a1a] transition-all duration-500"
            style={{ fontFamily: "'Inter', sans-serif" }}>
            Let's Talk <ArrowRight className="w-4 h-4" />
          </Link>
        </FadeIn>
      </section>
    </div>
  );
};

export default JewelryManufacturingPage;
